import React, { useState, useEffect } from "react";
import {
  Form,
  Typography,
  Input,
  Card,
  Alert,
  Flex,
  Button,
  Skeleton,
  Divider,
  Spin,
  Space,
} from "antd";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import "../App.css";

import {
  clearAll,
  placeOrder,
  setOrderFormData,
  setPabiliPadalaDetails,
} from "../redux/feature/order";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import axiosInstance from "../helpers/axios";
import formatCurrency from "../helpers/formatCurrency";
import { FaMapPin, FaMapMarkedAlt } from "react-icons/fa";

import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const { Title, Text } = Typography;
const { TextArea } = Input;

const schema = yup
  .object({
    fullName: yup.string().required("Full name is required"),
    contactNumber: yup
      .string()
      .required("Contact number is required")
      .matches(/^[0-9]+$/, "Contact number must contain only digits")
      .length(11, "Contact number must be exactly 11 digits"),
    note: yup.string().required("Note is required"),
  })
  .required();

const PabiliPadalaCheckOut = () => {
  const { pabiliPadalaDetails, checkoutLoading, orderFormData, changeDefault } =
    useSelector((state) => state.order);
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");
  const contactNumber = searchParams.get("contactNumber");
  const note = searchParams.get("note");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [greeting, setGreeting] = useState("");
  const [riderCheckerLoading, setRiderCheckerLoading] = useState(true);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [locationLoading, setLocationLoading] = useState(false);
  const [distance, setDistance] = useState(0);
  const [hasRider, setHasRider] = useState(false);
  const [isLocked, setIsLocked] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [keyError, setKeyError] = useState(false);

  const initialValues = {
    fullName: "",
    contactNumber: "",
    note: "",
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (name && contactNumber && note) {
      dispatch(
        setOrderFormData({
          fullName: name,
          contactNumber: contactNumber,
          note: note,
        }),
      );
    }
  }, [name, contactNumber, note, dispatch]);

  useEffect(() => {
    if (orderFormData) {
      setValue("fullName", orderFormData.fullName || "");
      setValue("contactNumber", orderFormData.contactNumber || "");
      setValue("note", orderFormData.note || "");
    }
  }, [orderFormData, setValue]);

  const hasDropOffLocation =
    pabiliPadalaDetails?.finalCustomerDetails?.dropOffLocation?.coordinates
      ?.latitude &&
    pabiliPadalaDetails?.finalCustomerDetails?.dropOffLocation?.coordinates
      ?.longitude;

  const hasPickupLocation =
    pabiliPadalaDetails?.finalCustomerDetails?.pickupLocation?.coordinates
      ?.latitude &&
    pabiliPadalaDetails?.finalCustomerDetails?.pickupLocation?.coordinates
      ?.longitude;

  const isValid = Boolean(hasDropOffLocation) && Boolean(hasPickupLocation);

  const onSubmitKey = () => {
    setKeyError(false);

    if (inputValue === "d0rydelivery2026") {
      setIsLocked(false);
    } else {
      setKeyError(true);
    }
  };

  const getCurrentLocation = () => {
    try {
      if (navigator.geolocation) {
        setLocationLoading(true);

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;

            // const responseCustomer = await axios.get(
            //   `https://nominatim.openstreetmap.org/reverse`,
            //   {
            //     params: {
            //       lat: Number(latitude),
            //       lon: Number(longitude),
            //       format: "json",
            //     },
            //   }
            // );

            const { data: customerLocation } = await axios.get(
              `https://api.dorydelivery.com/api/utility/get-province-city?latitude=${Number(
                latitude,
              )}&longitude=${Number(longitude)}`,
            );

            const pabiliDetails = {
              dropOffLocation: {
                locationText: `${customerLocation.result.city}, ${customerLocation.result.province}`,
                coordinates: {
                  latitude: Number(latitude),
                  longitude: Number(longitude),
                },
              },
            };

            dispatch(
              setPabiliPadalaDetails({
                finalCustomerDetails: { ...pabiliDetails, psid: 1 },
              }),
            );
            setLocationLoading(false);
          },
          (error) => {
            setLocationLoading(false);
            if (error.code === error.PERMISSION_DENIED) {
              alert(
                "Location permission is required for this feature. Please allow location access.",
              );
            } else {
              alert("An error occurred while accessing your location.");
            }
          },
        );
      }
    } catch (error) {
      alert("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    if (!hasDropOffLocation) {
      getCurrentLocation();
    }
  }, [hasDropOffLocation]);

  useEffect(() => {
    if (isValid) {
      const fetchDeliveryFee = async () => {
        await getDeliveryFee();
      };

      fetchDeliveryFee();
    }
  }, [isValid]);

  useEffect(() => {
    const currentHour = new Date().getHours();

    if (currentHour < 12) {
      setGreeting("Good Morning!");
    } else if (currentHour >= 12 && currentHour < 18) {
      setGreeting("Good Afternoon!");
    } else {
      setGreeting("Good Evening!");
    }
  }, []);

  const onCheckout = async (values) => {
    try {
      const custLat = Number(
        pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
          .latitude,
      );

      const custLng = Number(
        pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
          .longitude,
      );

      const { data } = await axios.get(
        `https://api.dorydelivery.com/api/utility/get-province-city?latitude=${custLat}&longitude=${custLng}`,
      );

      const customerDetails = {
        fullName: values.fullName,
        contactNumber: values.contactNumber,
        address:
          pabiliPadalaDetails?.finalCustomerDetails?.dropOffLocation
            ?.locationText,
        province: data.result.province,
        city: data.result.city,
        selectedMerchant: 1,
        coordinates: { latitude: custLat, longitude: custLng },
        pabiliDetails: {
          ...pabiliPadalaDetails.finalCustomerDetails,
          note: values.note,
        },
      };

      const payload = {
        total_price: parseFloat(0),
        partner_merchants_id: 1,
        total_markup: parseFloat(0),
        order: [],
        customerDetails: customerDetails,
        delivery_fee: deliveryFee,
        distance,
        psid: pabiliPadalaDetails.psid,
      };

      const resultAction = await dispatch(placeOrder(payload));

      if (placeOrder.fulfilled.match(resultAction)) {
        dispatch(clearAll());
        navigate("/success", {
          state: { orderNumber: resultAction.payload.order_number },
          replace: true,
        });
      } else {
        console.error(">>>> order failed:", resultAction);
      }
    } catch (error) {
      console.log("something went wrong");
    }
  };

  const getDeliveryFee = async () => {
    try {
      setRiderCheckerLoading(true);

      const { data } = await axiosInstance().post(`/api/orders/delivery-fee`, {
        merchants_id: 1,

        customer_latitude:
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .latitude,

        customer_longitude:
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .longitude,

        customer_province:
          pabiliPadalaDetails.finalCustomerDetails.pickUpLocationRiderToCheck
            .province,

        customer_city:
          pabiliPadalaDetails.finalCustomerDetails.pickUpLocationRiderToCheck
            .city,

        pabili_details: pabiliPadalaDetails.finalCustomerDetails,
      });

      setDeliveryFee(data.delivery_fee);
      setHasRider(data.hasRider);
      setDistance(data.distance);
      setRiderCheckerLoading(false);
    } catch (error) {
      setRiderCheckerLoading(false);
      console.log(">>> something went wrong fetching delivery fee");
    }
  };

  const handleNavigate = (type, lat, lng) => {
    const currentFormData = getValues();

    dispatch(setOrderFormData(currentFormData));

    let finalLat = "";
    let finalLng = "";

    if (type === "pickup") {
      if (pabiliPadalaDetails?.finalCustomerDetails?.pickupLocation) {
        finalLat =
          pabiliPadalaDetails.finalCustomerDetails.pickupLocation.coordinates
            .latitude;

        finalLng =
          pabiliPadalaDetails.finalCustomerDetails.pickupLocation.coordinates
            .longitude;
      } else {
        finalLat =
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .latitude;

        finalLng =
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .longitude;
      }
    } else if (type === "dropoff") {
      if (pabiliPadalaDetails?.finalCustomerDetails?.dropOffLocation) {
        finalLat =
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .latitude;

        finalLng =
          pabiliPadalaDetails.finalCustomerDetails.dropOffLocation.coordinates
            .longitude;
      } else {
        finalLat =
          pabiliPadalaDetails.finalCustomerDetails.pickupLocation.coordinates
            .latitude;

        finalLng =
          pabiliPadalaDetails.finalCustomerDetails.pickupLocation.coordinates
            .longitude;
      }
    }

    navigate("/collect-location-map", {
      state: { type, lat: finalLat, lng: finalLng },
    });
  };

  return (
    <div
      style={{
        width: "100%",
        height: "auto",
        backgroundColor: "#fff",
      }}
    >
      {locationLoading && <Spin fullscreen />}

      <div style={{ paddingBottom: "70px" }}>
        <div
          style={{
            marginLeft: 18,
            position: "relative",
            display: "inline-block",
          }}
        >
          <Title
            level={2}
            style={{
              position: "relative",
              zIndex: 1,
              marginBottom: 0,
            }}
          >
            {greeting}
          </Title>
        </div>
        {!hasRider && !riderCheckerLoading && (
          <div style={{ margin: 16 }}>
            <Alert message="No Available Rider" type="error" showIcon />
          </div>
        )}
        <Form layout="vertical" onFinish={handleSubmit(onCheckout)}>
          <Card style={{ marginLeft: 16, marginRight: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <Controller
                control={control}
                name="fullName"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Form.Item
                    label={
                      <label
                        htmlFor={"fullName"}
                        style={{ fontWeight: "bold", fontSize: 18 }}
                      >
                        Full name
                      </label>
                    }
                    required={true}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      allowClear
                      onChange={(e) => {
                        onChange(e);
                      }}
                      value={value}
                      size="large"
                    />
                  </Form.Item>
                )}
              />
              {errors.fullName && (
                <Text type="danger">{errors.fullName.message}</Text>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <Controller
                control={control}
                name="contactNumber"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Form.Item
                    label={
                      <label
                        htmlFor={"contactNumber"}
                        style={{ fontWeight: "bold", fontSize: 18 }}
                      >
                        Contact number
                      </label>
                    }
                    required={true}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      placeholder="ex. 09123456789"
                      allowClear
                      onChange={(e) => {
                        onChange(e);
                      }}
                      value={value}
                      size="large"
                    />
                  </Form.Item>
                )}
              />
              {errors.contactNumber && (
                <Text type="danger">{errors.contactNumber.message}</Text>
              )}
            </div>

            <div style={{ paddingTop: 12, paddingBottom: 12 }}>
              <div
                style={{
                  padding: 12,
                  border: "1px solid #ddd",
                  borderRadius: 8,
                  backgroundColor: "#fff",
                }}
              >
                {/* Pickup Location */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FaMapPin size={14} style={{ color: "#00BF63" }} />
                  <div>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 15,
                        fontWeight: "bold",
                        lineHeight: 1.2,
                      }}
                    >
                      Pickup location (Saan kukunin)
                    </Text>

                    <div
                      onClick={() => {
                        handleNavigate(
                          "pickup",
                          pabiliPadalaDetails.finalCustomerDetails
                            .dropOffLocation.coordinates.latitude,
                          pabiliPadalaDetails.finalCustomerDetails
                            .dropOffLocation.coordinates.longitude,
                        );
                      }}
                      style={{
                        cursor: "pointer",
                        ...(pabiliPadalaDetails?.finalCustomerDetails
                          ?.pickupLocation?.locationText
                          ? {
                              fontSize: 15,
                              fontWeight: "bold",
                              color: "#00BF63", // Normal green when set
                              display: "block",
                            }
                          : {
                              display: "inline-block",
                              backgroundColor: "#FFEDED", // Light red if missing
                              color: "#FF4D4F", // Red when required
                              fontSize: 15,
                              fontWeight: "bold",
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: "1px solid #FF4D4F",
                              boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.1)",
                              textAlign: "center", // Only when not set
                              transition: "0.3s ease-in-out", // Only when not set
                              animation: !pabiliPadalaDetails
                                ?.finalCustomerDetails?.pickupLocation
                                ?.locationText
                                ? "pound 1.5s infinite ease-in-out"
                                : "none",
                            }),
                      }}
                      className={!hasPickupLocation ? "pound-animation" : ""}
                    >
                      <Text
                        style={{
                          fontSize: 16,
                          fontWeight: "bold",
                          lineHeight: 1.2,
                          display: "block",
                          color: "black",
                        }}
                      >
                        {pabiliPadalaDetails?.finalCustomerDetails
                          ?.pickupLocation?.locationText
                          ? pabiliPadalaDetails?.finalCustomerDetails
                              ?.pickupLocation?.locationText
                          : ""}
                      </Text>

                      <Text
                        style={{
                          fontSize: 15,
                          fontWeight: "bold",
                          lineHeight: 1.2,
                          display: "block",
                          color: pabiliPadalaDetails?.finalCustomerDetails
                            ?.pickupLocation?.locationText
                            ? "#00BF63" // Green when set
                            : "#FF4D4F", // Red when required
                          cursor: "pointer", // Indicate it's clickable
                          textDecoration: "underline", // Emphasize clickability
                        }}
                      >
                        {!pabiliPadalaDetails?.finalCustomerDetails
                          ?.pickupLocation?.locationText
                          ? "⚠️ Saan bibilhin (Required)"
                          : "Select pick-up location"}
                      </Text>
                    </div>
                  </div>
                </div>

                <Divider dashed style={{ margin: "8px 0" }} />

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FaMapMarkedAlt size={14} style={{ color: "#00BF63" }} />
                  <div>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 15,
                        fontWeight: "bold",
                        lineHeight: 1.2,
                      }}
                    >
                      Drop-off location (Saan dadalhin)
                    </Text>

                    <div
                      onClick={() => {
                        handleNavigate(
                          "dropoff",
                          pabiliPadalaDetails.finalCustomerDetails
                            .dropOffLocation.coordinates.latitude,
                          pabiliPadalaDetails.finalCustomerDetails
                            .dropOffLocation.coordinates.longitude,
                        );
                      }}
                      style={{
                        cursor: "pointer",
                      }}
                    >
                      <div>
                        <Text
                          style={{
                            fontSize: 16,
                            fontWeight: "bold",
                            lineHeight: 1.2,
                            display: "block",
                            color: "black",
                          }}
                        >
                          {pabiliPadalaDetails?.finalCustomerDetails
                            ?.dropOffLocation?.locationText
                            ? pabiliPadalaDetails?.finalCustomerDetails
                                ?.dropOffLocation?.locationText
                            : ""}
                        </Text>
                      </div>
                      {!changeDefault && (
                        <div
                          style={{
                            marginTop: 4,
                            marginBottom: 4,
                            backgroundColor: "#F0F0F0",
                            color: "black",
                            borderRadius: 20,
                            padding: "4px 8px",
                            fontSize: 12,
                            display: "inline-block",
                          }}
                        >
                          Current location
                        </div>
                      )}

                      <Text
                        style={{
                          fontSize: 15,
                          fontWeight: "bold",
                          lineHeight: 1.2,
                          display: "block",
                          color: "#00BF63",
                        }}
                      >
                        {"Change drop-off location"}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Controller
              name="note"
              control={control}
              render={({ field }) => (
                <>
                  <Form.Item
                    label={
                      <label
                        htmlFor={"contactNumber"}
                        style={{ fontWeight: "bold", fontSize: 18 }}
                      >
                        Note
                      </label>
                    }
                    required={true}
                    style={{ marginBottom: 0 }}
                  >
                    <TextArea
                      {...field}
                      rows={6}
                      placeholder={`Store name: \nItem 1 name, Quantity \nItem 2 name, Quantity \n\n-- Additional instructions --`}
                    />
                  </Form.Item>
                  {errors.note && (
                    <p style={{ color: "red" }}>{errors.note.message}</p>
                  )}
                </>
              )}
            />

            {/*  */}
            <Space.Compact style={{ width: "100%", marginTop: 12 }}>
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Enter Key"
              />
              <Button
                style={{
                  borderColor: "#00BF63",
                  color: "#00BF63",
                  fontWeight: "bold",
                }}
                onClick={onSubmitKey}
              >
                Submit
              </Button>
            </Space.Compact>
            {keyError && <p style={{ color: "red" }}>Incorrect Key</p>}
            {/*  */}

            {isValid && (
              <div
                style={{
                  backgroundColor: "#f3f4f6",
                  padding: 16,
                  borderRadius: 16,
                  marginTop: 12,
                }}
              >
                <Flex vertical>
                  <Flex justify="space-between">
                    <Text strong>Delivery Fee</Text>
                    {riderCheckerLoading ? (
                      <Skeleton.Button
                        active
                        style={{ width: 50, height: 16 }}
                      />
                    ) : (
                      <Text strong>{formatCurrency(deliveryFee)}</Text>
                    )}
                  </Flex>

                  {/* <Flex justify="space-between">
                  <Text strong>Total</Text>
                  <Text strong>{formatCurrency(deliveryFee)}</Text>
                </Flex> */}
                </Flex>
              </div>
            )}
          </Card>

          <Button
            block
            type="primary"
            htmlType="submit"
            disabled={
              !hasRider || checkoutLoading || riderCheckerLoading || isLocked
            }
            loading={checkoutLoading}
            style={{
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1000,
              width: "90%",
              padding: 22,
              borderRadius: 12,
              fontWeight: "bold",
              background: "#00BF63",
            }}
          >
            Place Order
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default PabiliPadalaCheckOut;
